import {QuestionModelV3, SubjectModelV2, SupplementModelV1} from '@albert-io/models';
import makeConstants from 'lib/makeConstants';
import {List} from 'immutable';

import {
  TranslatedQuestion,
  TranslatedSupplement,
  // AlternateContentLanguage,
  // ALTERNATE_CONTENT_LANGUAGES,
  TranslationCoverage
} from './Translation.types';

const questionTypesArr = [
  'multiple-choice',
  'snippet-selection',
  'free-entry',
  'free-response',
  'fill-in-the-blank',
  'two-way',
  'passage-correction',
  'text-highlight',
  'graph-contains'
] as const;

type QuestionTypes = (typeof questionTypesArr)[number];

const questionTypes = makeConstants(...questionTypesArr);

interface modelPayload {
  question: QuestionModelV3;
  subject: SubjectModelV2;
  supplements: List<SupplementModelV1>;
}

export const getTranslationCoverage = (
  translatedInfo: TranslatedQuestion | null,
  translatedSupplements: TranslatedSupplement[],
  models: modelPayload
): TranslationCoverage => {
  const {question, supplements} = models;

  const type: QuestionTypes = question.getQuestionType();

  let fieldCoverage: TranslationCoverage;

  switch (type) {
    case questionTypes['multiple-choice']:
      fieldCoverage = checkMultipleChoiceFields(translatedInfo, translatedSupplements, models);
      break;
    case questionTypes['snippet-selection']:
      fieldCoverage = checkSnippetSelectionFields(translatedInfo, translatedSupplements, models);
      break;
    case questionTypes['free-entry']:
      fieldCoverage = checkFreeEntryFields(translatedInfo, translatedSupplements, models);
      break;
    case questionTypes['free-response']:
      fieldCoverage = checkFreeResponseFields(translatedInfo, translatedSupplements, models);
      break;
    case questionTypes['fill-in-the-blank']:
      fieldCoverage = checkFillInBlankFields(translatedInfo, translatedSupplements, models);
      break;
    case questionTypes['two-way']:
      fieldCoverage = checkTwoWayFields(translatedInfo, translatedSupplements, models);
      break;
    case questionTypes['passage-correction']:
      fieldCoverage = checkPassageCorrectionFields(translatedInfo, translatedSupplements, models);
      break;
    case questionTypes['text-highlight']:
      fieldCoverage = checkTextHighlightFields(translatedInfo, translatedSupplements, models);
      break;
    case questionTypes['graph-contains']:
      fieldCoverage = checkGraphFields(translatedInfo, translatedSupplements, models);
      break;
    default:
      fieldCoverage = defaultTranslationCoverage(translatedInfo);
  }

  const translationSettings = getTranslationSettings(models);
  const fullSettings =
    translationSettings.question && (translationSettings.supplement || supplements.size === 0);
  const partialSettings = translationSettings.question || translationSettings.supplement;

  switch (fieldCoverage) {
    case 'full':
      if (fullSettings) return 'full';
      if (partialSettings) return 'partial';
      return 'none';
    case 'partial': {
      if (partialSettings) {
        return 'partial';
      }
      return 'none';
    }
    case 'none':
    default:
      return fieldCoverage;
  }
};

const checkMultipleChoiceFields = (
  translationInfo: TranslatedQuestion | null,
  translatedSupplements: TranslatedSupplement[],
  models: modelPayload
) => {
  if (!translationInfo) return 'none';

  const {question, supplements} = models;

  const hasBasicTranslations = hasBasicFields(translationInfo);
  const translatedOptionFields = getOptionsInfo(translationInfo.translated_fields);
  const [hasFullSupplementTranslation, hasSupplementTranslations] =
    getSupplementTranslationCoverage(translatedSupplements, supplements);

  const options = question.getOptions();
  const hasTranslatedOptions = translatedOptionFields.length === options.size;

  if (translationInfo.translated_fields.length > 1 || hasSupplementTranslations) {
    if (
      translationInfo.meets_translation_requirements &&
      hasBasicTranslations &&
      hasTranslatedOptions &&
      hasFullSupplementTranslation
    ) {
      return 'full';
    }

    return 'partial';
  }
  return 'none';
};

const checkSnippetSelectionFields = (
  translationInfo: TranslatedQuestion | null,
  translatedSupplements: TranslatedSupplement[],
  models: modelPayload
) => {
  if (!translationInfo) return 'none';
  const {supplements} = models;

  const hasBasicTranslations = hasBasicFields(translationInfo);
  const [hasFullSupplementTranslation, hasSupplementTranslations] =
    getSupplementTranslationCoverage(translatedSupplements, supplements);
  const hasSnippetPromptTranslation = translationInfo.translated_fields.some(
    (info) => info.field === 'snippet_prompt'
  );

  if (translationInfo.translated_fields.length > 0 || hasSupplementTranslations) {
    if (
      translationInfo.meets_translation_requirements &&
      hasBasicTranslations &&
      hasFullSupplementTranslation &&
      hasSnippetPromptTranslation
    ) {
      return 'full';
    }

    return 'partial';
  }

  return 'none';
};

const checkFreeEntryFields = (
  translationInfo: TranslatedQuestion | null,
  translatedSupplements: TranslatedSupplement[],
  models: modelPayload
) => {
  if (!translationInfo) return 'none';

  const {supplements, question} = models;

  const hasBasicTranslations = hasBasicFields(translationInfo);
  const [hasFullSupplementTranslation, hasSupplementTranslations] =
    getSupplementTranslationCoverage(translatedSupplements, supplements);

  const inputs = question.getInputs();

  const inputFields = getInputsInfo(translationInfo.translated_fields);
  const hasTranslatedInputs = inputFields.length === inputs.size;

  if (translationInfo.translated_fields.length > 0 || hasSupplementTranslations) {
    if (
      translationInfo.meets_translation_requirements &&
      hasBasicTranslations &&
      hasFullSupplementTranslation &&
      hasTranslatedInputs
    ) {
      return 'full';
    }

    return 'partial';
  }

  return 'none';
};

const checkFreeResponseFields = (
  translationInfo: TranslatedQuestion | null,
  translatedSupplements: TranslatedSupplement[],
  models: modelPayload
) => {
  if (!translationInfo) return 'none';

  const {supplements} = models;

  const hasBasicTranslations = hasBasicFields(translationInfo);
  const [hasFullSupplementTranslation, hasSupplementTranslations] =
    getSupplementTranslationCoverage(translatedSupplements, supplements);

  if (translationInfo.translated_fields.length > 0 || hasSupplementTranslations) {
    if (
      translationInfo.meets_translation_requirements &&
      hasBasicTranslations &&
      hasFullSupplementTranslation
    ) {
      return 'full';
    }
    return 'partial';
  }

  return 'none';
};

const checkFillInBlankFields = (
  translationInfo: TranslatedQuestion | null,
  translatedSupplements: TranslatedSupplement[],
  models: modelPayload
) => {
  if (!translationInfo) return 'none';

  const {question, supplements} = models;

  const hasBasicTranslations = hasBasicFields(translationInfo);
  const [hasFullSupplementTranslation, hasSupplementTranslations] =
    getSupplementTranslationCoverage(translatedSupplements, supplements);
  const hadDropdownText = translationInfo.translated_fields.some((info) =>
    info.field.includes('dropdown_text')
  );

  const dropdownChoices = getQuestionOptionChoices(question);
  const translatedDropdownChoiceFields = getDropdownsInfo(translationInfo.translated_fields);
  const hasTranslatedOptions = translatedDropdownChoiceFields.length === dropdownChoices.size;

  if (translationInfo.translated_fields.length > 0 || hasSupplementTranslations) {
    if (
      translationInfo.meets_translation_requirements &&
      hasBasicTranslations &&
      hasFullSupplementTranslation &&
      hadDropdownText &&
      hasTranslatedOptions
    ) {
      return 'full';
    }

    return 'partial';
  }

  return 'none';
};

const checkTwoWayFields = (
  translationInfo: TranslatedQuestion | null,
  translatedSupplements: TranslatedSupplement[],
  models: modelPayload
) => {
  if (!translationInfo) return 'none';

  const {supplements} = models;

  const hasBasicTranslations = hasBasicFields(translationInfo);
  const [hasFullSupplementTranslation, hasSupplementTranslations] =
    getSupplementTranslationCoverage(translatedSupplements, supplements);

  const hasHeaderLeftTranslation = translationInfo.translated_fields.some(
    (info) => info.field === 'header_left'
  );
  const hasHeaderRightTranslation = translationInfo.translated_fields.some(
    (info) => info.field === 'header_right'
  );

  const hasHeaderTranslations = hasHeaderLeftTranslation && hasHeaderRightTranslation;

  if (translationInfo.translated_fields.length > 0 || hasSupplementTranslations) {
    if (
      translationInfo.meets_translation_requirements &&
      hasBasicTranslations &&
      hasFullSupplementTranslation &&
      hasHeaderTranslations
    ) {
      return 'full';
    }

    return 'partial';
  }

  return 'none';
};

const checkPassageCorrectionFields = (
  translationInfo: TranslatedQuestion | null,
  translatedSupplements: TranslatedSupplement[],
  models: modelPayload
) => {
  if (!translationInfo) return 'none';
  const {supplements} = models;

  const hasBasicTranslations = hasBasicFields(translationInfo);
  const [hasFullSupplementTranslation, hasSupplementTranslations] =
    getSupplementTranslationCoverage(translatedSupplements, supplements);
  const hasUncorrectedTranslation = translationInfo.translated_fields.some(
    (info) => info.field === 'uncorrected_text'
  );
  if (translationInfo.translated_fields.length > 0 || hasSupplementTranslations) {
    if (
      translationInfo.meets_translation_requirements &&
      hasBasicTranslations &&
      hasFullSupplementTranslation &&
      hasUncorrectedTranslation
    ) {
      return 'full';
    }

    return 'partial';
  }

  return 'none';
};

const checkTextHighlightFields = (
  translationInfo: TranslatedQuestion | null,
  translatedSupplements: TranslatedSupplement[],
  models: modelPayload
) => {
  if (!translationInfo) return 'none';
  const {supplements} = models;

  const hasBasicTranslations = hasBasicFields(translationInfo);
  const [hasFullSupplementTranslation, hasSupplementTranslations] =
    getSupplementTranslationCoverage(translatedSupplements, supplements);
  const hasHighlightPromptTranslation = translationInfo.translated_fields.some(
    (info) => info.field === 'highlight_prompt'
  );

  if (translationInfo.translated_fields.length > 0 || hasSupplementTranslations) {
    if (
      translationInfo.meets_translation_requirements &&
      hasBasicTranslations &&
      hasFullSupplementTranslation &&
      hasHighlightPromptTranslation
    ) {
      return 'full';
    }

    return 'partial';
  }

  return 'none';
};

const checkGraphFields = (
  translationInfo: TranslatedQuestion | null,
  translatedSupplements: TranslatedSupplement[],
  models: modelPayload
) => {
  if (!translationInfo) return 'none';
  const {supplements} = models;

  const hasBasicTranslations = hasBasicFields(translationInfo);
  const [hasFullSupplementTranslation, hasSupplementTranslations] =
    getSupplementTranslationCoverage(translatedSupplements, supplements);

  if (translationInfo.translated_fields.length > 0 || hasSupplementTranslations) {
    if (
      translationInfo.meets_translation_requirements &&
      hasBasicTranslations &&
      hasFullSupplementTranslation
    )
      return 'full';

    return 'partial';
  }

  return 'none';
};

const hasBasicFields = (translationInfo: TranslatedQuestion) => {
  const translatedFields = [...translationInfo.translated_fields];

  const hasPromptTranslation = translatedFields.some((info) => info.field === 'prompt');
  const hasTitleTranslation = translatedFields.some((info) => info.field === 'title');
  const hasSolutionTextTranslation = translatedFields.some(
    (info) => info.field === 'solution_text'
  );

  return hasPromptTranslation && hasTitleTranslation && hasSolutionTextTranslation;
};

const getOptionsInfo = (translatedFields: TranslatedQuestion['translated_fields']) => {
  return translatedFields.filter((info) => info.field.includes('option'));
};

const getDropdownsInfo = (translatedFields: TranslatedQuestion['translated_fields']) => {
  return translatedFields.filter((info) => {
    return info.field.includes('dropdowns');
  });
};

const getInputsInfo = (translatedFields: TranslatedQuestion['translated_fields']) => {
  return translatedFields.filter((info) => {
    return info.field.includes('inputs');
  });
};

const getQuestionOptionChoices = (question: QuestionModelV3) => {
  const dropdowns = question.getDropdowns();
  let choices = List();
  dropdowns.forEach((dropdown) => {
    const dropdownChoices = dropdown.getChoices();
    choices = choices.push(...dropdownChoices.toJS());
  });

  return choices;
};

const defaultTranslationCoverage = (translationInfo: any) => {
  if (translationInfo.meets_translation_requirements) return 'full';
  // if false
  if (translationInfo.translated_fields.length > 0) return 'partial';
  return 'none';
};

export const getTranslationSettings = (models: modelPayload) => {
  const {question, subject, supplements} = models;
  const subjectQuestionTranslationDisabled =
    subject != null && subject.isTranslateQuestions?.() === false;
  const subjectSupplementTranslationDisabled =
    subject != null && subject.isTranslateSupplements?.() === false;
  const questionSupplementTranslationEnabled =
    question.isTranslateSupplements?.() && supplements.toArray().some((s) => s.isTranslate?.());

  const questionTranslationEnabled =
    !subjectQuestionTranslationDisabled && question.isTranslate?.();
  const supplementTranslationEnabled =
    !subjectSupplementTranslationDisabled && questionSupplementTranslationEnabled;

  return {
    question: questionTranslationEnabled,
    supplement: supplementTranslationEnabled
  };
};

export const getSupplementTranslationCoverage = (
  translatedSupplements: TranslatedSupplement[],
  supplements
) => {
  if (supplements.size === 0) {
    return [true, true];
  }

  const correctlyTranslatedSupplements = translatedSupplements.filter(
    ({meets_translation_requirements, translate}) => meets_translation_requirements && translate
  );

  const partialTranslation = translatedSupplements.some(
    ({meets_translation_requirements, translate}) => meets_translation_requirements && translate
  );

  return [correctlyTranslatedSupplements.length === supplements.size, partialTranslation] as const;
};
